import {
  UnbundlingFareInfographic,
  computeUnbundlingInfographic
} from "@silversea-ssc/ui-components-react";
import { useLingui } from "@lingui/react/macro";

import React, { useCallback, useMemo } from "react";
import { unbundlingInfographicConfigForCruiseGroup } from "features/fare/infographic/infographic-config-for-cruise-group";
import {
  withStandardInclusions,
  unbundlingInfographicGroupLabels
} from "../../utils";
import { CruiseGroupCode } from "@silversea-ssc/web-cruise-groups";
import { InclusionCode } from "@silversea-ssc/web-inclusions";
import { useInclusionsDrawer } from "features/inclusions/InclusionsDrawer/InclusionsDrawerProvider";
import type { TokenValues } from "@silversea-ssc/dotcom-read-model-types/Tokens";
import { FareCode } from "@silversea-ssc/web-fares";
import { fareCodeLabel } from "features/fare/fares";
import { toCancellationPolicyFareInfographicStepInfo } from "../fare-cancellation-policy-step";
import { useFarePenaltiesModal } from "features/cancellation-policy/FarePenaltiesModal/FarePenaltiesModalProvider";

type UnbundlingFareCardInfographicProps = {
  fareCode: FareCode;
  cruiseGroup: CruiseGroupCode | undefined;
  enabledInclusions: readonly InclusionCode[];
  inclusionsContentTokens: TokenValues;
  disableInclusionDrawer?: boolean;
};

const UnbundlingFareCardInfographic: React.FC<
  UnbundlingFareCardInfographicProps
> = ({
  fareCode,
  cruiseGroup,
  enabledInclusions,
  inclusionsContentTokens,
  disableInclusionDrawer = false,
  ...expandProps
}) => {
  const { t } = useLingui();

  const infographicConfig =
    unbundlingInfographicConfigForCruiseGroup(cruiseGroup);
  const { onInclusionClick, inclusionsContent } = useInclusionsDrawer();

  const { onRequestOpenPenaltiesModal, remotePaymentTermByFareCode } =
    useFarePenaltiesModal();
  const paymentTerms = remotePaymentTermByFareCode(fareCode);

  const inclusionCodes = withStandardInclusions(enabledInclusions);
  const cancellationPolicyStepInfo =
    toCancellationPolicyFareInfographicStepInfo(t, paymentTerms);

  const infographicSteps = useMemo(
    () =>
      computeUnbundlingInfographic(
        infographicConfig,
        inclusionCodes,
        inclusionsContent,
        unbundlingInfographicGroupLabels(t, 0),
        cancellationPolicyStepInfo
      ),
    [
      infographicConfig,
      inclusionCodes,
      inclusionsContent,
      t,
      cancellationPolicyStepInfo
    ]
  );
  const onClick = useCallback(
    (stepsGroupId) => {
      if (stepsGroupId === "cancellation-policy") {
        onRequestOpenPenaltiesModal({
          fareCode: fareCode
        });
        return;
      }

      onInclusionClick({
        inclusionsContentTokens: inclusionsContentTokens,
        selectedStepGroupId: stepsGroupId,
        title: t(fareCodeLabel(fareCode)),
        steps: infographicSteps
      });
    },
    [
      onInclusionClick,
      inclusionsContentTokens,
      t,
      fareCode,
      infographicSteps,
      onRequestOpenPenaltiesModal
    ]
  );

  return (
    <React.Fragment>
      <UnbundlingFareInfographic
        steps={infographicSteps}
        onInclusionClick={disableInclusionDrawer ? undefined : onClick}
        {...expandProps}
      />
    </React.Fragment>
  );
};

export default UnbundlingFareCardInfographic;
